/**
 * <FocttaCoreBadge> - small pill that visibly tags a page section as
 * being augmented with live FOCTTA core data (vs degraded standalone
 * mode). Drop it next to any heading whose data flows through the
 * sdk-factory → FOCTTA core path.
 *
 * Three states:
 *   - integrated:    "FOCTTA Core" green pill, dot animation
 *   - standalone:    "Standalone" muted pill
 *   - unknown:       hidden (don't surface noise during initial load)
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CircleDot, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getFocttaCoreStatus } from '@/lib/api';

type ProbeState = 'loading' | 'integrated' | 'standalone' | 'error';

let cachedState: ProbeState = 'loading';
let cachedAt = 0;

/**
 * 60-second front-end cache so 5 instances of the badge on the same
 * page don't fire 5 status calls.
 */
async function probeStatus(): Promise<ProbeState> {
  const now = Date.now();
  if (cachedAt > now - 60_000 && cachedState !== 'loading' && cachedState !== 'error') {
    return cachedState;
  }
  try {
    const s = await getFocttaCoreStatus();
    cachedState = s.connected ? 'integrated' : 'standalone';
    cachedAt = now;
    return cachedState;
  } catch {
    cachedState = 'error';
    cachedAt = now;
    return cachedState;
  }
}

export function FocttaCoreBadge({ label = 'FOCTTA Core' }: { label?: string }) {
  const [state, setState] = useState<ProbeState>(cachedState);
  useEffect(() => {
    let mounted = true;
    void probeStatus().then((s) => {
      if (mounted) setState(s);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (state === 'loading' || state === 'error') return null;
  if (state === 'standalone') {
    return (
      <Badge variant="outline" className="text-[10px] font-mono opacity-70">
        Standalone
      </Badge>
    );
  }
  return (
    <Badge
      variant="glass"
      className="text-[10px] font-mono bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
    >
      <CircleDot className="h-2.5 w-2.5 mr-1 inline animate-pulse" />
      {label}
    </Badge>
  );
}

/**
 * Hook variant - call sites that need the integration state for branching
 * logic (e.g. show/hide a section) read this rather than render the pill.
 */
export function useFocttaCoreIntegrated(): boolean | null {
  const [state, setState] = useState<ProbeState>(cachedState);
  useEffect(() => {
    let mounted = true;
    void probeStatus().then((s) => {
      if (mounted) setState(s);
    });
    return () => {
      mounted = false;
    };
  }, []);
  if (state === 'loading' || state === 'error') return null;
  return state === 'integrated';
}

/**
 * Inline "Configure" link to take the operator to the Connect wizard.
 */
export function FocttaCoreConfigureLink() {
  return (
    <Link
      to="/admin/integrations/foctta-core"
      className="text-[10px] font-mono opacity-70 hover:opacity-100 inline-flex items-center gap-1 underline-offset-2 hover:underline"
    >
      Configure <ExternalLink className="h-2.5 w-2.5" />
    </Link>
  );
}
